/**
 * Centralized styling hook for inline detail sub-tables.
 *
 * Returns Tailwind class names that match the parent table's density + textSize,
 * selected via JS conditional instead of group-data CSS selectors (which don't
 * cascade into <div> cells nested inside <td colSpan>).
 *
 * Pixel values for min-height are applied via inline style because Tailwind
 * min-h-* classes constructed dynamically can get purged from the CSS bundle.
 *
 * Row heights derive from DENSITY_ROW_HEIGHT (the single source of truth for
 * parent table row heights in useDataGridVirtualization.ts).
 *
 * See: docs/frappe-debug/75-inline-detail-row-height-nine-iteration-debugging.md
 */

import { useMemo } from "react";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import type { TableDensity, TableTextSize } from "@/lib/store/preferencesStore";
import { DENSITY_ROW_HEIGHT } from "@/components/data-grid/useDataGridVirtualization";

// --------------------------------------------------------------------------
// Sub-table row height overrides (user-tuned for readability in compact rows)
// Comfortable + spacious match parent exactly.
// Compact + default are intentionally slightly larger than parent rows
// because sub-table content (text-only, no avatar) needs extra vertical
// breathing room to look balanced.
// --------------------------------------------------------------------------
const SUB_TABLE_ROW_HEIGHT: Record<TableDensity, number> = {
  compact: 45,  // parent: 36 — tuned up for readability
  default: 50,  // parent: 44 — tuned up for readability
  comfortable: DENSITY_ROW_HEIGHT.comfortable, // 56 — matches parent exactly
  spacious: DENSITY_ROW_HEIGHT.spacious,       // 72 — matches parent exactly
};

// Head heights in pixels — matches table.tsx h-* classes
// (h-6=24, h-8=32, h-9=36, h-10=40)
const HEAD_HEIGHT: Record<TableDensity, number> = {
  compact: 24,
  default: 32,
  comfortable: 36,
  spacious: 40,
};

// --------------------------------------------------------------------------
// Shared row-level classes (centralized — used by SubTableRow components)
// --------------------------------------------------------------------------
export const SUB_TABLE_HEAD_ROW_CLASS =
  "bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700";

export const SUB_TABLE_DATA_ROW_CLASS =
  "border-b border-neutral-100 dark:border-neutral-800 hover:bg-blue-50/30 dark:hover:bg-neutral-800/30";

export const SUB_TABLE_SECTION_HEADER_CLASS =
  "bg-neutral-100/80 dark:bg-neutral-800/50 border-b border-neutral-200 dark:border-neutral-700 font-semibold uppercase tracking-wider text-muted-foreground";

export const SUB_TABLE_SKELETON_CLASS =
  "bg-neutral-200 dark:bg-neutral-700 animate-pulse rounded-md";

export const SUB_TABLE_ACTION_BUTTON_CLASS =
  "flex items-center gap-1 text-muted-foreground hover:text-foreground";

// --------------------------------------------------------------------------
// Hook return type
// --------------------------------------------------------------------------
export interface TableStyles {
  /** Classes for header cells — matches TableHead */
  headCellClass: string;
  /** Classes for data cells — matches TableCell */
  dataCellClass: string;
  /** Pixel min-height for header rows (applied via inline style) */
  headMinHeight: number;
  /** Pixel min-height for data rows (applied via inline style) */
  dataMinHeight: number;
  density: TableDensity;
  textSize: TableTextSize;
}

export function useTableStyles(): TableStyles {
  const density = usePreferencesStore((s) => s.tableDensity ?? "comfortable");
  const textSize = usePreferencesStore((s) => s.tableTextSize ?? "medium");

  return useMemo(() => {
    // Text size classes — exact same as table.tsx group-data-[textsize=*]
    const headTextClass =
      textSize === "small" ? "text-[10px]" :
      textSize === "large" ? "text-[13px]" :
      "text-xs"; // medium

    const cellTextClass =
      textSize === "small" ? "text-xs" :
      textSize === "large" ? "text-[15px]" :
      "text-sm"; // medium

    // Cell padding — exact same as table.tsx group-data-[density=*]
    const cellPadClass =
      density === "compact" ? "py-0 px-1.5" :
      density === "spacious" ? "py-3 px-3" :
      density === "comfortable" ? "py-2 px-3" :
      "py-1.5 px-3"; // default

    // Head padding
    const headPadClass =
      density === "compact" ? "px-1.5" : "px-3";

    return {
      headCellClass: [
        headPadClass,
        headTextClass,
        "flex items-center",
        "text-left font-medium text-muted-foreground uppercase tracking-wider",
        "whitespace-nowrap overflow-hidden text-ellipsis",
        "border-r border-neutral-200 dark:border-neutral-700 last:border-r-0",
      ].join(" "),
      dataCellClass: [
        cellPadClass,
        cellTextClass,
        "flex items-center",
        "overflow-hidden text-ellipsis whitespace-nowrap",
        "border-r border-neutral-200 dark:border-neutral-700 last:border-r-0",
      ].join(" "),
      headMinHeight: HEAD_HEIGHT[density],
      dataMinHeight: SUB_TABLE_ROW_HEIGHT[density],
      density,
      textSize,
    };
  }, [density, textSize]);
}
